import React from "react";
import {Alert} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import type {ContractOpportunity, OpportunityKind} from "../contractExplorerTypes.ts";
import {KIND_LABEL} from "../contractExplorerTypes.ts";
import {formatDate, formatMoney, opportunityFigure} from "../contractExplorerFormat.ts";
import {useContractOpportunitiesQuery} from "../useContractOpportunitiesQuery.ts";
import {ConfidenceBadge} from "./ConfidenceBadge.tsx";

type Props = {
    bagId: number;
    contractId: number;
    kind: OpportunityKind | null;
    currency: string;
    onOpen: (opportunityId: number) => void;
};

/** The bundle's opportunities of one kind, or all, the most at stake first; a row opens the finding. */
export const OpportunitiesGrid: React.FC<Props> = ({bagId, contractId, kind, currency, onOpen}) => {
    const {data, isFetching, error} = useContractOpportunitiesQuery(bagId, contractId, kind);

    const columns = React.useMemo<GridColDef<ContractOpportunity>[]>(() => [
        {field: 'kind', headerName: 'Kind', minWidth: 210, flex: 1, sortable: false,
            renderCell: ({row}) => (
                <Tag label={KIND_LABEL[row.kind]} color={row.category === 'leakage' ? 'success' : 'warning'}
                     variant="outlined"/>
            )},
        {field: 'amount_at_risk', headerName: currency ? `Saving or at risk (${currency})` : 'Saving or at risk',
            width: 170, align: 'right', headerAlign: 'right',
            renderCell: ({row}) => {
                const figure = opportunityFigure(row);
                return <span className="ci-mono" title={figure.label}>{formatMoney(figure.value)}</span>;
            }},
        {field: 'supplier', headerName: 'Supplier', minWidth: 160, flex: 1},
        {field: 'entities', headerName: 'Buying entities', minWidth: 160, flex: 1, sortable: false,
            valueGetter: (_value, row) => row.entities.join('; ')},
        {field: 'period_from', headerName: 'Period', width: 210, sortable: false,
            renderCell: ({row}) => `${formatDate(row.period_from)} → ${formatDate(row.period_to)}`},
        {field: 'spend', headerName: currency ? `Spend (${currency})` : 'Spend', width: 120, align: 'right',
            headerAlign: 'right', renderCell: ({row}) => <span className="ci-mono">{formatMoney(row.spend)}</span>},
        {field: 'confidence', headerName: 'Confidence', width: 110, align: 'right', headerAlign: 'right',
            renderCell: ({row}) => <ConfidenceBadge value={row.confidence} title="The specialist's confidence in this finding"/>},
        {field: 'summary', headerName: 'Summary', minWidth: 260, flex: 2, sortable: false,
            renderCell: ({row}) => <span className="ci-reason" title={row.summary}>{row.summary}</span>},
    ], [currency]);

    if (error) return <Alert severity="error">The opportunities could not be loaded: {error.message}</Alert>;
    return (
        <div className="ci-grid">
            <CustomDataGrid
                rows={data ?? []}
                columns={columns}
                autoHeight
                rowSize="small"
                autoRowHeight
                loading={isFetching && !data}
                hideFooter={(data?.length ?? 0) <= 25}
                pageSizeOptions={[25, 50]}
                onRowClick={({row}) => onOpen(row.id)}
                disableRowSelectionOnClick
            />
        </div>
    );
};
